import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import io from "socket.io-client";
import { FaClock, FaComments } from "react-icons/fa";
import { SkeletonLobby } from "../../Skeleton";
import API_URL from '../../../utils/api';
import "./PreBreakoutEvaluation.css";
export default function PreBreakoutEvaluation() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [partner, setPartner] = useState(null);
  const [userId] = useState(() => sessionStorage.getItem("userId") || localStorage.getItem("userId"));
  const [responses, setResponses] = useState({
    anticipatedEnjoyment: 5,
    anticipatedConnection: 5,
    anticipatedComfort: 5,
    anticipatedInterest: 5,
    anticipatedOpenness: 5
  });
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [partnerSubmitted, setPartnerSubmitted] = useState(false);
  const [socket, setSocket] = useState(null);
  const [pairKey, setPairKey] = useState(null);
  
  // Helper to create consistent pair key
  const getPairKey = (userId1, userId2) => {
    const ids = [String(userId1), String(userId2)].sort();
    return ids.join('-');
  };
  // Fetch session and determine partner (with retry if partner not found)
  useEffect(() => {
    let cancelled = false;
    let retryTimer = null;

    const fetchSession = async (isRetry = false) => {
      try {
        if (!isRetry) {
          const response = await fetch(`${API_URL}/api/sessions/${sessionId}`);
          const data = await response.json();
          if (data.members && typeof data.members === 'string') {
            data.members = JSON.parse(data.members);
          }
          if (!cancelled) setSession(data);
        }
        // Get pairing information
        const pairResponse = await fetch(`${API_URL}/api/sessions/${sessionId}/start-breakout`, {
          method: "POST",
          headers: { "Content-Type": "application/json" }
        });
        const pairData = await pairResponse.json();
        if (pairData.ok && pairData.pairs) {
          const myPair = pairData.pairs.find(p => 
            p.some(member => String(member.user_id || member.userId) === String(userId))
          );
          if (myPair) {
            const partnerMember = myPair.find(member => 
              String(member.user_id || member.userId) !== String(userId)
            );
            if (!cancelled) {
              setPartner(partnerMember);
              if (partnerMember) {
                const partnerId = partnerMember.user_id || partnerMember.userId;
                setPairKey(getPairKey(userId, partnerId));
              }
            }
            return; // success
          }
        }
        // Partner not found yet — retry after 2s (snapshot may not be ready)
        if (!cancelled) {
          console.warn('[PreEval] Partner not found in pairs, retrying in 2s...');
          retryTimer = setTimeout(() => fetchSession(true), 2000);
        }
      } catch (error) {
        if (!cancelled) {
          retryTimer = setTimeout(() => fetchSession(true), 2000);
        }
      }
    };
    fetchSession();
    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [sessionId, userId]);
  // Socket.io connection for real-time updates
  useEffect(() => {
    const newSocket = io(API_URL);
    setSocket(newSocket);
    newSocket.emit("join-session", sessionId);

    const currentPairKey = pairKey;
    const partnerId = partner?.user_id || partner?.userId;

    // Listen for partner submission - check if it's from our pair
    newSocket.on("pre-eval-submitted", (data) => {
      // Only count as "partner submitted" if it was for OUR pair
      if (!currentPairKey) return;
      if (String(data.userId) !== String(partnerId)) return;
      if (data.pairKey !== currentPairKey) return;
      setPartnerSubmitted(true);
    });

    // Listen for pair-ready signal - check if it's our pair
    newSocket.on("pre-eval-pair-ready", (data) => {
      // Check if this pair-ready is for our pair
      if (currentPairKey && data.pairKey === currentPairKey) {
        navigate(`/breakout-room/${sessionId}`);
      }
      // Also check using userIds array
      const userIdStr = String(userId);
      const partnerIdStr = String(partnerId || '');
      if (data.userIds && (data.userIds.includes(userIdStr) || data.userIds.includes(partnerIdStr))) {
        navigate(`/breakout-room/${sessionId}`);
      }
    });
    
    // Keep backward compatibility with old event (for 2-person sessions)
    newSocket.on("pre-eval-both-ready", () => {
      navigate(`/breakout-room/${sessionId}`);
    });
    return () => {
      newSocket.disconnect();
    };
  }, [sessionId, partner, pairKey, navigate, userId]);
  const handleSliderChange = (field, value) => {
    setResponses(prev => ({ ...prev, [field]: parseInt(value) }));
  };
  const handleSubmit = async () => {
    try {
      // Save pre-breakout evaluation
      await fetch(`${API_URL}/api/evaluations/pre-breakout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId,
          userId,
          partnerId: partner?.user_id || partner?.userId,
          responses,
          timestamp: new Date().toISOString()
        })
      });
      // Mark as submitted
      setHasSubmitted(true);
      // Notify partner via socket
      if (socket) {
        socket.emit("pre-eval-submit", {
          sessionId,
          userId,
          partnerId: partner?.user_id || partner?.userId
        });
      }
      // If partner already submitted, navigate immediately
      if (partnerSubmitted) {
        navigate(`/breakout-room/${sessionId}`);
      }
    } catch (error) {
      }
  };
  if (!session || !partner) {
    return (
      <div className="pre-eval-root">
        <SkeletonLobby />
      </div>
    );
  }
  const questions = [
    {
      field: 'anticipatedEnjoyment',
      label: 'How much do you think you will enjoy this conversation?',
      lowLabel: 'Not at all',
      highLabel: 'Very much'
    },
    {
      field: 'anticipatedConnection',
      label: 'How connected do you expect to feel with your partner?',
      lowLabel: 'Not connected',
      highLabel: 'Very connected'
    },
    {
      field: 'anticipatedComfort',
      label: 'How comfortable do you expect to feel during this conversation?',
      lowLabel: 'Very uncomfortable',
      highLabel: 'Very comfortable'
    },
    {
      field: 'anticipatedInterest',
      label: 'How interesting do you expect this conversation to be?',
      lowLabel: 'Not interesting',
      highLabel: 'Very interesting'
    },
    {
      field: 'anticipatedOpenness',
      label: 'How open do you expect to be in sharing with your partner?',
      lowLabel: 'Not open',
      highLabel: 'Very open'
    }
  ];
  return (
    <div className="pre-eval-root">
      {/* Header */}
      <div className="pre-eval-header">
        <h2>Before Your Breakout Session</h2>
        <p className="pre-eval-subtitle">Affective Forecasting Assessment</p>
      </div>
      {/* Partner Info */}
      <div className="pre-eval-partner-card">
        <div className="partner-info-label">You'll be speaking with:</div>
        <div className="partner-info-display">
          <div className="partner-avatar-large">
            {partner.name?.charAt(0).toUpperCase()}
          </div>
          <div className="partner-name-large">{partner.name}</div>
        </div>
        <div className="duration-info">Duration: 10-15 minutes</div>
      </div>
      {/* Instructions */}
      <div className="pre-eval-instructions">
        <div className="instruction-icon"><FaComments /></div>
        <div className="instruction-text">
          Before your conversation, please share your expectations. 
          There are no right or wrong answers, we're simply interested in your predictions.
        </div>
      </div>
      {/* Questions */}
      <div className="pre-eval-questions">
        {questions.map((q, index) => (
          <div key={q.field} className="eval-question-card">
            <div className="question-number">{index + 1}</div>
            <div className="question-content">
              <div className="question-label">{q.label}</div>
              <div className="slider-container">
                <div className="slider-labels">
                  <span className="slider-label-low">{q.lowLabel}</span>
                  <span className="slider-value">{responses[q.field]}</span>
                  <span className="slider-label-high">{q.highLabel}</span>
                </div>
                <input
                  type="range"
                  min="1"
                  max="10"
                  value={responses[q.field]}
                  onChange={(e) => handleSliderChange(q.field, e.target.value)}
                  className="eval-slider"
                />
                <div className="slider-scale">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                    <span key={num} className="scale-marker">{num}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Submit Button */}
      <div className="pre-eval-submit-section">
        {!hasSubmitted ? (
          <>
            <button 
              className="pre-eval-submit-btn"
              onClick={handleSubmit}
            >
              Continue to Breakout Room →
            </button>
            <div className="submit-note">
              Your responses will help us track the accuracy of your affective forecasting
            </div>
          </>
        ) : (
          <div className="pre-eval-waiting">
            <div className="waiting-spinner"><FaClock /></div>
            <div className="waiting-text">
              Waiting for <strong>{partner?.name}</strong> to complete their evaluation...
            </div>
            <div className="waiting-subtext">
              You'll be redirected to the breakout room automatically
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
